import React from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat, MessageSquare } from 'lucide-react';
import { toUserZoned } from '../../../shared/lib/tzDate';
import { STAGES } from '../lib/taskGrid';

// slice 02.1 · paso b.4 — la vista TABLERO (kanban) de la pantalla de tareas. Presentacional:
// recibe las tareas + `on*` de drag. Una columna por etapa (`STAGES`), tarjetas arrastrables.
export const TaskKanbanView = ({ tasks, systemUsers = [], userTz, scrollRef, onColDragOver, onColDrop, onCardDragStart, onCardDragEnd, onCardClick }) => {
    const { t: translate } = useTranslation('tasks');

    // Tag de fecha relativa de la tarjeta (mismo criterio que `getRelativeDateDisplay`, en `userTz`).
    const dateTag = (dateStr) => {
        const parts = dateStr ? dateStr.split('-') : [];
        if (parts.length !== 3) return null;
        const today = toUserZoned(new Date(), userTz);
        today.setHours(0, 0, 0, 0);
        const target = new Date(today);
        target.setFullYear(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        target.setHours(0, 0, 0, 0);
        const diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return { text: translate('kanban.dateTag.today'), color: '#ef4444' };
        if (diffDays > 0) {
            let text;
            if (diffDays === 1) text = translate('kanban.dateTag.oneDayAgo');
            else if (diffDays < 7) text = translate('kanban.dateTag.daysAgo', { count: diffDays });
            else if (diffDays < 14) text = translate('kanban.dateTag.oneWeekAgo');
            else if (diffDays < 21) text = translate('kanban.dateTag.twoWeeksAgo');
            else text = translate('kanban.dateTag.moreThanThreeWeeksAgo');
            return { text, color: '#ef4444' };
        }
        const absDiff = Math.abs(diffDays);
        let text;
        if (absDiff === 1) text = translate('kanban.dateTag.inOneDay');
        else if (absDiff < 7) text = translate('kanban.dateTag.inDays', { count: absDiff });
        else if (absDiff < 14) text = translate('kanban.dateTag.inOneWeek');
        else if (absDiff < 21) text = translate('kanban.dateTag.inTwoWeeks');
        else text = translate('kanban.dateTag.inDays', { count: absDiff });
        return { text, color: '#10b981' };
    };

    return (
        <div className="kanban-panel" ref={scrollRef}>
            {STAGES.map(stage => {
                const stageTasks = tasks.filter(t => t.stage === stage);
                return (
                    <div
                        key={stage}
                        className={`kanban-col ${stage.replace(/\s+/g, '-')}-col-border`}
                        onDragOver={onColDragOver}
                        onDrop={(e) => onColDrop(e, stage)}
                    >
                        <div className="kanban-col-header">
                            <span className={`kanban-col-title-dot ${stage.replace(/\s+/g, '-')}`}></span>
                            <h3 className="kanban-col-title">{translate(`stages.${stage}`)}</h3>
                            <span className="kanban-col-count">{stageTasks.length}</span>
                        </div>
                        <div className="kanban-cards-container">
                            {stageTasks.length > 0 ? (
                                stageTasks.map(task => {
                                    const tags = task.tags ? task.tags.split(',').map(s => s.trim()).filter(Boolean) : [];
                                    const assignees = task.assignee ? task.assignee.split(',').map(s => s.trim()).filter(Boolean) : [];
                                    const visibleAssignees = assignees.slice(0, 4);
                                    const commentsCount = Array.isArray(task.comments) ? task.comments.length : 0;
                                    const tag = dateTag(task.date);

                                    return (
                                        <div
                                            key={task.id}
                                            draggable
                                            onDragStart={(e) => onCardDragStart(e, task.id)}
                                            onDragEnd={onCardDragEnd}
                                            className={`kanban-card priority-${task.priority}`}
                                            onClick={(e) => onCardClick(task, e)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {tags.length > 0 && (
                                                <div className="kanban-card-tags">
                                                    {tags.map((t, idx) => (
                                                        <span key={idx} className="kanban-tag">{t}</span>
                                                    ))}
                                                </div>
                                            )}
                                            <p className="kanban-card-title">
                                                {task.recurrent_interval > 0 && <Repeat size={10} style={{ marginRight: 'var(--space-1)', display: 'inline-block', verticalAlign: 'middle', color: '#a0a0a0' }} />}
                                                {task.title || translate('common.untitled')}
                                            </p>
                                            <div className="kanban-card-footer">
                                                <div className="kanban-assignees-container">
                                                    <div className="kanban-assignees-stack">
                                                        {visibleAssignees.map((user, idx) => {
                                                            const foundUser = systemUsers.find(u => u.nick.toLowerCase() === user.toLowerCase());
                                                            const avatarUrl = (foundUser?.['link-profile-img-thumb'] || foundUser?.['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user}`;
                                                            return (
                                                                <img
                                                                    key={idx}
                                                                    src={avatarUrl}
                                                                    alt={user}
                                                                    title={user}
                                                                    className="user-avatar"
                                                                    loading="lazy"
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <div className="kanban-footer-right">
                                                    {tag && (
                                                        <span style={{ fontSize: '9px', color: tag.color, fontWeight: '400' }}>
                                                            {tag.text}
                                                        </span>
                                                    )}
                                                    {task.priority && (
                                                        <span className={`priority-badge ${task.priority}`}>{translate(`priority.${task.priority}`)}</span>
                                                    )}
                                                    <div className="kanban-comments-count">
                                                        <MessageSquare size={12} />
                                                        <span>{commentsCount}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="kanban-empty-msg">{translate('kanban.dropHere')}</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default TaskKanbanView;
